import {useTranslation} from "react-i18next";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Tooltip from "@mui/material/Tooltip";
import {colors} from "../../theme/tokens";

export type TernaryFlagValue = boolean | null;

type TernaryFeatureFlagProps = {
    value: TernaryFlagValue;
    onChange: (value: TernaryFlagValue) => void;
    compact?: boolean;
    disabled?: boolean;
    "aria-label"?: string;
};

type Segment = {
    value: TernaryFlagValue;
    shortLabelKey: string;
    labelKey: string;
    activeColor: string;
    activeBackground: string;
};

const SEGMENTS: Segment[] = [
    {
        value: true,
        shortLabelKey: "FLAGS_TERNARY_ON_SHORT",
        labelKey: "FLAGS_TERNARY_ON",
        activeColor: colors.success,
        activeBackground: colors.successTint,
    },
    {
        value: null,
        shortLabelKey: "FLAGS_TERNARY_INHERIT_SHORT",
        labelKey: "FLAGS_TERNARY_INHERIT",
        activeColor: colors.body,
        activeBackground: colors.navHover,
    },
    {
        value: false,
        shortLabelKey: "FLAGS_TERNARY_OFF_SHORT",
        labelKey: "FLAGS_TERNARY_OFF",
        activeColor: colors.error,
        activeBackground: colors.errorTint,
    },
];

/** Segmented ON / INH / OFF control: a stored override, or inheritance of the global default (`null`). */
export function TernaryFeatureFlag({
                                       value,
                                       onChange,
                                       compact = false,
                                       disabled = false,
                                       "aria-label": ariaLabel,
                                   }: TernaryFeatureFlagProps) {
    const {t} = useTranslation();

    return (
            <Box
                    component="span"
                    role="group"
                    aria-label={ariaLabel ?? t("FLAGS_TERNARY_ARIA")}
                    sx={{
                        display: "inline-flex",
                        border: `1px solid ${colors.segmentBorder}`,
                        borderRadius: "4px",
                        overflow: "hidden",
                        flexShrink: 0,
                        opacity: disabled ? 0.5 : 1,
                    }}
            >
                {SEGMENTS.map((segment, index) => {
                    const active = value === segment.value;
                    return (
                            <Tooltip key={segment.shortLabelKey} title={t(segment.labelKey)} disableInteractive>
                                {/* The span keeps the tooltip working when the segment is disabled. */}
                                <Box component="span" sx={{display: "inline-flex", borderLeft: index > 0 ? `1px solid ${colors.segmentBorder}` : "none"}}>
                                <ButtonBase
                                        onClick={() => onChange(segment.value)}
                                        aria-pressed={active}
                                        aria-label={t(segment.labelKey)}
                                        disabled={disabled}
                                        sx={{
                                            width: compact ? 34 : 36,
                                            height: 22,
                                            fontSize: "10px",
                                            fontWeight: 600,
                                            letterSpacing: "0.02em",
                                            color: active ? segment.activeColor : colors.placeholder,
                                            backgroundColor: active ? segment.activeBackground : "#fff",
                                            "&:hover": {color: active ? segment.activeColor : colors.body},
                                            "&.Mui-focusVisible": {outline: `2px solid ${colors.primary}`, outlineOffset: -2},
                                        }}
                                >
                                    {t(segment.shortLabelKey)}
                                </ButtonBase>
                                </Box>
                            </Tooltip>
                    );
                })}
            </Box>
    );
}
